import { inject, Injectable } from '@angular/core';
import { TopInterventionControllerApi } from '../../../api/services/top-intervention-controller-api';
import { firstValueFrom } from 'rxjs';
import { Storage } from '@ionic/storage';
import { TopInterventionTypeListDto } from '../../../api/models/top-intervention-type-list-dto';
import { TypeInterventionSecuriteDto } from '../../../api/models/type-intervention-securite-dto';
import { QuestionSecuriteDto } from '../../../api/models/question-securite-dto';
import _ from 'lodash';
import { NetworkProvider } from '../network/network';
import { PostTopIntervention$Params } from '../../../api/fn/top-intervention-controller/post-top-intervention';
import { PostTopInterventionQuestionReponse } from '../../../api/models/post-top-intervention-question-reponse';
import { UserService } from '../auth/user.service';
import { LoggerFactory } from '../logger/logger-factory.service';

export type QuestionReponseSecuriteDto = QuestionSecuriteDto & {
  response?: boolean;
  commentaireTechnicien?: string;
};

@Injectable({ providedIn: 'root' })
export class PortailSecuriteProvider {
  private readonly TOP_INTERVENTION_DATA_KEY = 'TOP_INTERVENTION_DATA';
  private readonly TOP_INTERVENTION_TO_SEND = 'TOP_INTERVENTION_TO_SEND';
  private readonly NB_QUESTIONS_POUR_TOP: number = 5;

  private logger = inject(LoggerFactory).getInstance(this, true);
  private networkProvider = inject(NetworkProvider);
  private userService = inject(UserService);
  storage: Storage = inject(Storage);
  topInterventionApi: TopInterventionControllerApi = inject(TopInterventionControllerApi);

  loadDonneesTopIntervention() {
    firstValueFrom(this.topInterventionApi.getAllQuestions())
      .then((data) => {
        this.storage
          .set(this.TOP_INTERVENTION_DATA_KEY, data)
          .then(() => console.log('Données top intervention chargées'));
      })
      .catch((error) => {
        console.error('Erreur de chargement des données top intervention', error);
      });
  }

  async getDonneesTopIntervention(): Promise<TopInterventionTypeListDto> {
    return await this.storage.get(this.TOP_INTERVENTION_DATA_KEY);
  }

  choisirQuestions(typeIntervention: TypeInterventionSecuriteDto): QuestionSecuriteDto[] {
    const selectedQuestions: QuestionSecuriteDto[] = [];
    const allQuestions: QuestionSecuriteDto[] = typeIntervention.questions;
    if (_.isEmpty(typeIntervention.questions)) {
      return [];
    }

    selectedQuestions.push(
      ...allQuestions.filter((question: QuestionSecuriteDto) => {
        return question.questionObligatoire === true;
      })
    );
    if (selectedQuestions.length >= this.NB_QUESTIONS_POUR_TOP) {
      return selectedQuestions;
    }

    const questionNonObligatoires: QuestionSecuriteDto[] = allQuestions.filter((question: QuestionSecuriteDto) => {
      return question.questionObligatoire !== true;
    });
    selectedQuestions.push(
      ..._.sampleSize(questionNonObligatoires, this.NB_QUESTIONS_POUR_TOP - selectedQuestions.length)
    );
    return selectedQuestions;
  }

  async postTopIntervention(idTopInterventionType: number, reponses: QuestionReponseSecuriteDto[], numeroOt: string) {
    const reponsesToSend: PostTopInterventionQuestionReponse[] = reponses.map((r) => {
      return {
        idTopInterventionTypeQuestion: r.idTopInterventionTypeQuestion,
        reponse: r.response,
        commentaire: r.commentaireTechnicien,
      };
    });

    const params: PostTopIntervention$Params = {
      body: {
        codeBt: numeroOt,
        dkCodeCreateur: this.userService.getDkCodeRessource(),
        idTopInterventionType: idTopInterventionType,
        reponses: reponsesToSend,
      },
    };
    if (await this.networkProvider.isReallyConnected()) {
      firstValueFrom(this.topInterventionApi.postTopIntervention(params))
        .then(() => {
          this.logger.debug('postTopIntervention - Top intervention envoyé avec succès');
        })
        .catch((error) => {
          this.logger.error(error, "postTopIntervention - Erreur lors de l'envoi du top intervention", params);
        });
    } else {
      const topToSend: PostTopIntervention$Params[] = (await this.storage.get(this.TOP_INTERVENTION_TO_SEND)) || [];
      topToSend.push(params);
      await this.storage.set(this.TOP_INTERVENTION_TO_SEND, topToSend);
    }
  }

  async retryPostTopIntervention() {
    if (await this.networkProvider.isReallyConnected()) {
      const topToSend: PostTopIntervention$Params[] = (await this.storage.get(this.TOP_INTERVENTION_TO_SEND)) || [];
      if (topToSend.length > 0) {
        for (let top of topToSend) {
          firstValueFrom(this.topInterventionApi.postTopIntervention(top))
            .then(() => {
              this.logger.debug('retryPostTopIntervention - Top intervention envoyé avec succès');
            })
            .catch((error) => {
              this.logger.error(error, "retryPostTopIntervention - Erreur lors de l'envoi du top intervention", top);
            });
        }
        await this.storage.remove(this.TOP_INTERVENTION_TO_SEND);
      }
    }
  }
}
